import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Ruler, RefreshCw, Satellite } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PricingControls } from "@/components/measurements/report/PricingControls";
import { QuotePreview } from "@/components/measurements/report/QuotePreview";
import { AdditionalTradesBuilder } from "@/components/measurements/AdditionalTradesBuilder";

export interface RbPricingState {
  squares: number | null;
  pricePerSq: number;
  material: string;
  wastePercent: number;
  complexityPercent: number;
}

interface Props {
  address: string;
  pricing: RbPricingState;
  setPricing: (updater: (p: RbPricingState) => RbPricingState) => void;
  onNext: (result: RbQuoteResult) => void;
}

export interface RbQuoteResult {
  roofTotal: number;
  tradesSubtotal: number;
  commissionAmount: number;
  grandTotal: number;
  tradeCount: number;
}

const MATERIALS = [
  { key: "asphalt-30", label: "30-Year Architectural Shingles", pricePerSq: 95 },
  { key: "asphalt-50", label: "50-Year Premium Shingles", pricePerSq: 130 },
  { key: "metal-standing", label: "Standing Seam Metal", pricePerSq: 350 },
  { key: "metal-stone", label: "Stone-Coated Metal", pricePerSq: 280 },
  { key: "tile-clay", label: "Clay Tile", pricePerSq: 450 },
  { key: "flat-tpo", label: "TPO Flat Roof", pricePerSq: 180 },
];

export function StepRbPrice({ address, pricing, setPricing, onNext }: Props) {
  const [loadingMeasure, setLoadingMeasure] = useState(false);
  const [measureSource, setMeasureSource] = useState<string>("");
  const [extras, setExtras] = useState({
    tradesSubtotal: 0,
    commissionAmount: 0,
    tradeCount: 0,
  });
  const selectedMaterial = MATERIALS.find((material) => material.key === pricing.material) ?? MATERIALS[0];

  const fetchMeasurement = async () => {
    if (!address.trim()) return;
    setLoadingMeasure(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke(
        "google-solar-measurement",
        {
          body: { address },
          headers: sessionData?.session?.access_token
            ? { Authorization: `Bearer ${sessionData.session.access_token}` }
            : undefined,
        }
      );
      if (error) throw error;
      const m = data?.measurement;
      const sq =
        data?.totalSquares ??
        m?.total_squares ??
        m?.totalSquares ??
        null;
      if (sq && sq > 0) {
        setPricing((p) => ({ ...p, squares: Math.round(sq * 10) / 10 }));
        setMeasureSource("Google Solar (satellite)");
        toast.success(`Measured ${sq.toFixed(1)} squares`);
      } else {
        setMeasureSource("estimate");
        toast.info("Couldn't auto-measure — adjust manually");
        setPricing((p) => ({ ...p, squares: p.squares ?? 25 }));
      }
    } catch (e: any) {
      console.error(e);
      setMeasureSource("estimate");
      toast.error("Measurement failed — enter squares manually");
      setPricing((p) => ({ ...p, squares: p.squares ?? 25 }));
    } finally {
      setLoadingMeasure(false);
    }
  };

  useEffect(() => {
    if (pricing.squares == null) fetchMeasurement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sq = pricing.squares ?? 0;
  const orderSq = useMemo(
    () => Math.ceil(sq * (1 + pricing.wastePercent / 100)),
    [sq, pricing.wastePercent]
  );
  const finalPrice = useMemo(
    () => Math.round(orderSq * pricing.pricePerSq * (1 + pricing.complexityPercent / 100)),
    [orderSq, pricing.pricePerSq, pricing.complexityPercent]
  );
  const materialCost = useMemo(() => orderSq * selectedMaterial.pricePerSq, [orderSq, selectedMaterial.pricePerSq]);
  const laborCost = useMemo(() => orderSq * 75, [orderSq]);
  const tearoffCost = useMemo(() => orderSq * 50, [orderSq]);
  const grandTotal = useMemo(
    () => finalPrice + extras.tradesSubtotal + extras.commissionAmount,
    [extras.commissionAmount, extras.tradesSubtotal, finalPrice]
  );

  return (
    <div className="px-4 pt-6 pb-8 sm:px-5 space-y-5 min-w-0 overflow-x-hidden">
      <div>
        <div className="text-xs font-semibold text-primary uppercase tracking-wider mb-1.5">
          Step 3 / 4
        </div>
        <h2 className="text-2xl font-bold tracking-tight">Price the roof</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Mobile Quick Quote with the same measurement and pricing controls from desktop.
        </p>
      </div>

      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 space-y-2 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Property</p>
            <p className="mt-1 text-sm font-medium leading-5 break-words">{address}</p>
          </div>
          <div className="shrink-0 rounded-full border border-primary/20 bg-background px-3 py-1 text-xs font-semibold text-primary">
            Step 3
          </div>
        </div>
      </div>

      {/* Measurement */}
      <div className="rounded-2xl border border-border/60 bg-card p-4 space-y-3 min-w-0">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Label className="text-xs font-semibold flex items-center gap-1.5">
            <Ruler className="h-3.5 w-3.5" /> Roof Size
          </Label>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={fetchMeasurement}
            disabled={loadingMeasure}
          >
            {loadingMeasure ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Re-measure
          </Button>
        </div>
        {loadingMeasure && pricing.squares == null ? (
          <div className="h-12 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <Satellite className="h-4 w-4" /> Measuring from satellite…
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 min-w-0">
              <Input
                type="number"
                inputMode="decimal"
                value={sq}
                onChange={(e) =>
                  setPricing((p) => ({ ...p, squares: Number(e.target.value) || 0 }))
                }
                className="h-12 min-w-0 text-2xl font-bold"
              />
              <span className="shrink-0 text-sm text-muted-foreground">squares</span>
            </div>
            {measureSource && (
              <p className="text-[11px] text-muted-foreground">Source: {measureSource}</p>
            )}
          </>
        )}
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-4 min-w-0 overflow-hidden">
        <PricingControls
          pricePerSq={pricing.pricePerSq}
          onPricePerSqChange={(value) => setPricing((p) => ({ ...p, pricePerSq: value }))}
          material={pricing.material}
          onMaterialChange={(value) => setPricing((p) => ({ ...p, material: value }))}
          materials={MATERIALS}
          wastePercent={pricing.wastePercent}
          onWasteChange={(value) => setPricing((p) => ({ ...p, wastePercent: value }))}
          complexityPercent={pricing.complexityPercent}
          onComplexityChange={(value) => setPricing((p) => ({ ...p, complexityPercent: value }))}
        />
      </div>

      <QuotePreview
        pricePerSq={pricing.pricePerSq}
        squares={sq}
        wastePercent={pricing.wastePercent}
        complexityPercent={pricing.complexityPercent}
        materialCost={materialCost}
        laborCost={laborCost}
        tearoffCost={tearoffCost}
      />

      <AdditionalTradesBuilder
        baseQuoteTotal={finalPrice}
        onChange={({ lines, tradesSubtotal, commissionAmount }) => {
          setExtras({
            tradesSubtotal,
            commissionAmount,
            tradeCount: lines.length,
          });
        }}
      />

      <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 space-y-2 min-w-0">
        <div className="flex items-start justify-between gap-4 text-sm">
          <span className="text-muted-foreground">Roof Quote</span>
          <span className="font-semibold tabular-nums text-right break-words">${finalPrice.toLocaleString()}</span>
        </div>
        <div className="flex items-start justify-between gap-4 text-sm">
          <span className="text-muted-foreground">Additional Products</span>
          <span className="font-semibold tabular-nums text-right break-words">${extras.tradesSubtotal.toLocaleString()}</span>
        </div>
        <div className="flex items-start justify-between gap-4 text-sm">
          <span className="text-muted-foreground">Commission / Markup</span>
          <span className="font-semibold tabular-nums text-right break-words">${extras.commissionAmount.toLocaleString()}</span>
        </div>
        <div className="flex items-end justify-between gap-4 pt-2 border-t border-primary/20">
          <span className="text-sm font-semibold">Grand Total</span>
          <span className="text-right text-3xl font-extrabold text-primary tabular-nums break-words">
            ${grandTotal.toLocaleString()}
          </span>
        </div>
      </div>

      <Button
        onClick={() =>
          onNext({
            roofTotal: finalPrice,
            tradesSubtotal: extras.tradesSubtotal,
            commissionAmount: extras.commissionAmount,
            grandTotal,
            tradeCount: extras.tradeCount,
          })
        }
        disabled={!sq || sq <= 0 || grandTotal <= 0}
        className="w-full h-12 text-base font-semibold"
        size="lg"
      >
        Continue to Financing
      </Button>
    </div>
  );
}
